import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
} from '@angular/core';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { DirectiveSuperclass } from '@sama/ng-core';
import { TaskWorkspaceStore } from '../../../../../../../core/h3ml/state/task-workspace-store';
import { OutputKeyboardShortcutService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/output-keyboard-shortcut.service';
import { PushAndTrackService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/push-and-track.service';
import { ShapeSelectionService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/shape-selection.service';
import { RootKeyboardShortcutService } from '../../../../../../../core/shared/keyboard-shortcuts/root-keyboard-shortcut.service';
import { ObjectlessShapeService } from '../../../../../../../core/shared/misc-services/objectless-shape.service';
import { getShapesForEventTracking } from '../../../../../../../cross-team-shared/annotation/shared/state/layers/shape-layer-state';
import {
  EventTrackingValueWithSource,
  expandShapeGroups,
  selectionToArray,
} from '../../../../../../../cross-team-shared/misc-utils';
import { ShapeOrGroupOrderItem } from '../../../../../../../cross-team-shared/state/shape-group-state';
import { outputAnnotationInfoStoreToken } from '../../../../annotation-providers';
import { AbstractAnnotationStore } from '../../../../state/root/abstract-annotation-store';
import { getNextSelectedObject } from '../../../../utils/list-utils';

@Component({
  selector: 'h3-delete-shapes-button',
  templateUrl: './delete-shapes-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DeleteShapesButtonComponent extends DirectiveSuperclass {
  private readonly shapesStore = inject(outputAnnotationInfoStoreToken)(
    'shapes',
  );
  private readonly outputAnnotationInfoStore = inject(
    outputAnnotationInfoStoreToken,
  );
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);

  tooltip: string;
  faTrash = faTrash;
  selectedShapeCount$ = this.shapeSelectionService.getSelectedShapeCount$();

  constructor(
    public store: AbstractAnnotationStore,
    private selectionService: ShapeSelectionService,
    private shapeSelectionService: ShapeSelectionService,
    private pushAndTrackService: PushAndTrackService,
    private objectlessShapeService: ObjectlessShapeService,
    private rootKeyboardShortcutService: RootKeyboardShortcutService,
    injector: Injector,
    outputKeyboardShortcutService: OutputKeyboardShortcutService,
  ) {
    super(injector);
    this.tooltip =
      'Delete' + this.rootKeyboardShortcutService.tooltipSuffix('deleteShapes');
    this.subscribeTo(
      outputKeyboardShortcutService.getEvent$('deleteShapes'),
      ({ eventTrackingValue }) => {
        this.delete({ ...eventTrackingValue, source: 'keyboard_shortcut' });
      },
    );
  }

  delete(eventTrackingValue: EventTrackingValueWithSource) {
    const selection = this.shapeSelectionService.getSelectionForOutput();
    const { shapeAndGroupOrder, groups } =
      this.outputAnnotationInfoStore.state();
    const indexesToDelete = selectionToArray(selection);

    if (
      this.taskWorkspaceStore.state().readOnly ||
      indexesToDelete.length === 0
    ) {
      return;
    }

    this.handleShapeDeletion(indexesToDelete, eventTrackingValue);
    // Handle selection before removing shapes or else selection will refer to
    // non-existing shapes
    this.handleShapeSelectionOnDelete(
      indexesToDelete,
      // @ts-ignore -- fix me
      expandShapeGroups(shapeAndGroupOrder, groups).reverse(),
    );
  }

  private handleShapeSelectionOnDelete(
    indexesToDelete: number[],
    orderShapesIndexes: ShapeOrGroupOrderItem[],
  ): void {
    const nextIndexesToSelect: number[] = [];
    if (indexesToDelete.length !== orderShapesIndexes.length) {
      // From the shapes selected for deletion, select the index of the one
      // closest to the end of the list
      const largestIndexToDelete = Math.max(
        ...indexesToDelete.map((index) =>
          orderShapesIndexes.findIndex((s) => s.index === index),
        ),
      );

      // Select the next shape from the index above that is not included for
      // deletion
      const nextIndexToSelect = getNextSelectedObject(
        orderShapesIndexes.map((_, i) => i),
        largestIndexToDelete,
        'forward',
      );
      nextIndexesToSelect.push(orderShapesIndexes[nextIndexToSelect].index);
    }

    this.selectionService.setSelection(nextIndexesToSelect);
  }

  private handleShapeDeletion(
    indexesToDelete: number[],
    eventTrackingValue: EventTrackingValueWithSource,
  ): void {
    const deletedShapesForEventTracking = getShapesForEventTracking(
      // @ts-ignore -- fix me
      this.shapesStore.state(),
      indexesToDelete,
    );

    this.objectlessShapeService.deleteShapes(indexesToDelete);

    this.pushAndTrackService.pushAndTrack({
      id: 'delete_shapes',
      bucket: 'adjustment',
      ...eventTrackingValue,
      shapes: deletedShapesForEventTracking,
    });
  }
}
